/**
 * Payment lock: remembers which coupon / pop-in entries have finished payment so
 * the wizard and resume pages can't be re-entered and edited after the fact.
 *
 * Why this exists: once a Pop-in is paid the backend rejects a second payment
 * ("You have already made payment"), but it still accepts location edits. Combined
 * with the browser Back button / bfcache restoring a stale, already-paid flow, a
 * user could add a third location to a 2-location Pop-in after paying. We persist a
 * small "paid" marker (survives clearAllData) and redirect those flows to the
 * success page instead of letting them edit.
 *
 * Scope is intentionally per-browser: it only blocks an entry the user just paid
 * for in this browser, so the email renew flow (a separately-paid pop-in) is
 * unaffected.
 */

const PAID_KEY = 'deliva_paid_entries';
const PENDING_KEY = 'deliva_pending_payment';

const toId = (value) => {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const readPaid = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PAID_KEY) || 'null');
    return {
      coupons: Array.isArray(parsed?.coupons) ? parsed.coupons : [],
      popins: Array.isArray(parsed?.popins) ? parsed.popins : []
    };
  } catch (e) {
    return { coupons: [], popins: [] };
  }
};

const writePaid = (data) => {
  try {
    localStorage.setItem(PAID_KEY, JSON.stringify(data));
  } catch (e) {
    // ignore storage failures (private mode / quota)
  }
};

/**
 * Mark an entry as paid. Either id may be omitted.
 * @param {{ couponId?: number|string|null, popinId?: number|string|null }} ids
 */
export const markEntryPaid = ({ couponId, popinId } = {}) => {
  const data = readPaid();
  const c = toId(couponId);
  const p = toId(popinId);
  let changed = false;
  if (c != null && !data.coupons.includes(c)) {
    data.coupons.push(c);
    changed = true;
  }
  if (p != null && !data.popins.includes(p)) {
    data.popins.push(p);
    changed = true;
  }
  if (changed) writePaid(data);
};

/**
 * @param {{ couponId?: number|string|null, popinId?: number|string|null }} ids
 * @returns {boolean} true if either id has already completed payment
 */
export const isEntryPaid = ({ couponId, popinId } = {}) => {
  const data = readPaid();
  const c = toId(couponId);
  const p = toId(popinId);
  return (
    (c != null && data.coupons.includes(c)) ||
    (p != null && data.popins.includes(p))
  );
};

/**
 * Remember the entry whose payment we are about to start (before redirecting to
 * PayFast). Promoted to "paid" once the user lands on the success page.
 * @param {{ couponId?: number|string|null, popinId?: number|string|null }} ids
 */
export const markPaymentPending = ({ couponId, popinId } = {}) => {
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ couponId: toId(couponId), popinId: toId(popinId) })
    );
  } catch (e) {
    // ignore
  }
};

/** Drop the pending marker (e.g. payment was cancelled, so retry stays allowed). */
export const clearPaymentPending = () => {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (e) {
    // ignore
  }
};

/** On payment success: turn the pending marker into a permanent paid marker. */
export const promotePendingToPaid = () => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return;
    const { couponId, popinId } = JSON.parse(raw) || {};
    markEntryPaid({ couponId, popinId });
  } catch (e) {
    // ignore
  } finally {
    clearPaymentPending();
  }
};
